/**
 * Gallery Variant Modal Component
 * Orchestrates variant selection and preloading for gallery artworks
 */

import { createGalleryVariantSelector } from './gallery-variant-selector.js';
import { createGalleryVariantPreloader } from './gallery-variant-preloader.js';

/**
 * @param {Function} func - Function to debounce
 * @param {number} wait - Delay in milliseconds
 * @returns {Function} Debounced function
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func.apply(this, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

function createGalleryVariantModalCore() {
  return {
    modal_open: false,
    clickOutsideHandler: null,
    focusTrapHandler: null,
    
    _cachedElements: {
      optionsContainer: null,
      galleryBuilder: null
    },
    
    _debouncedResetScroll: null,
    _debouncedOpenModal: null,

    init() {
      this._debouncedResetScroll = debounce(() => this.resetScrollPosition(), 100);
      this._debouncedOpenModal = debounce(() => this.openModal(), 50);

      this.$watch('$store.galleryUI.variant_modal_open', (open) => {
        this.modal_open = open;
        if (open) {
          this._debouncedOpenModal();
          this._debouncedResetScroll();
          setTimeout(() => this._debouncedResetScroll(), 350);
        }
      });

      this.$watch('$store.galleryCore.selected_artworks.length', (newLength, oldLength) => {
        if (this.modal_open && newLength > oldLength) {
          this._debouncedOpenModal();
          this.preloadNewArtworkVariants();
        }
      });

      this.$watch('$store.galleryCore.orientation', (newOrientation, oldOrientation) => {
        if (newOrientation !== oldOrientation) {
          this.preloaded_combinations.clear();

          if (this.modal_open) {
            this._debouncedOpenModal();
          }
        }
      });

    },

    openModal() {
      const store = this.$store.galleryCore;

      if (!store) {
        return;
      }

      if (!store.selected_artworks || store.selected_artworks.length === 0) {
        setTimeout(() => this.openModal(), 1000);
        return;
      }

      const validArtworks = store.selected_artworks.filter((artwork) =>
        artwork && artwork.id && artwork.variants && artwork.variants.length > 0
      );

      if (validArtworks.length === 0) {
        setTimeout(() => this.openModal(), 1000);
        return;
      }

      store.initVariantManager();

      const options = store.variant_manager.getCommonOptions(store.selected_artworks);

      this.available_sizes = store.variant_manager.sortOptions(options.sizes, 'size');
      this.available_frames = store.variant_manager.sortOptions(options.frames, 'frame');
      this.available_mounts = store.variant_manager.sortOptions(options.mounts, 'mount');

      if (!store.variant_selection.frame) {
        const defaultFrame = this.available_frames.includes('Black') ? 'Black' : this.available_frames[0];
        if (defaultFrame) {
          store.setVariantOption('frame', defaultFrame, { skipPreviewUpdate: true });
        }
      }

      if (!store.variant_selection.mount) {
        const defaultMount = this.available_mounts.includes('Border') ? 'Border' : this.available_mounts[0];
        if (defaultMount) {
          store.setVariantOption('mount', defaultMount, { skipPreviewUpdate: true });
        }
      }

      this.selection_in_progress = false;
      this.validateSelection();
      this.preloadLikelyVariants();

      this.$nextTick(() => {
        this.setupFocusTrap();
        this.resetScrollPosition();
      });

      if (this.clickOutsideHandler) {
        document.removeEventListener('click', this.clickOutsideHandler);
        this.clickOutsideHandler = null;
      }

      setTimeout(() => {
        this.clickOutsideHandler = (event) => this.handleClickOutside(event);
        document.addEventListener('click', this.clickOutsideHandler);
      }, 100);
    },


    closeModal() {
      const lastActiveIndex = this.$store.galleryUI.last_active_artwork_index_before_modal;

      this.$store.galleryUI.closeVariantModal();
      this.removeFocusTrap();
      this.modal_open = false;

      if (this.clickOutsideHandler) {
        document.removeEventListener('click', this.clickOutsideHandler);
        this.clickOutsideHandler = null;
      }

      // Reopen carousel with the last active artwork if it existed
      if (lastActiveIndex !== null) {
        this.$store.galleryUI.setActiveArtwork(lastActiveIndex);
        this.$store.galleryUI.last_active_artwork_index_before_modal = null;
      }
    },

    handleClickOutside(event) {
      if (this.$el.contains(event.target)) {
        return;
      }

      if (event.target.closest('.gallery-builder__layout-toggle')) {
        return;
      }

      if (event.target.closest('.gallery-builder__config-button button')) {
        return;
      }

      this.closeModal();
    },

    handleEscape() {
      if (this.modal_open) {
        this.closeModal();
      }
    },

    setupFocusTrap() {
      const modal = this.$el;
      if (!modal) return;

      const focusableElements = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      firstFocusable.focus();

      this.focusTrapHandler = (e) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
        } else if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      };

      modal.addEventListener('keydown', this.focusTrapHandler);
    },

    removeFocusTrap() {
      if (this.focusTrapHandler && this.$el) {
        this.$el.removeEventListener('keydown', this.focusTrapHandler);
        this.focusTrapHandler = null;
      }
    },

    resetScrollPosition() {
      if (!this._cachedElements.optionsContainer) {
        this._cachedElements.optionsContainer = this.$el?.querySelector('.gallery-variant-selection__options');
      }
      
      const optionsContainer = this._cachedElements.optionsContainer;
      if (optionsContainer) {
        optionsContainer.scrollTop = 0;
        optionsContainer.scrollTo(0, 0);
        optionsContainer.scrollTo({ top: 0, behavior: 'instant' });
      }
    },

    isMobile() {
      return window.innerWidth <= 989;
    },

    async handleAddToCart() {
      const payload = this.$store.galleryCore.prepareCartPayload();

      if (!payload || payload.length === 0) {
        console.warn('Gallery: No items to add to cart from mobile actions');
        return;
      }

      if (!this._cachedElements.galleryBuilder) {
        this._cachedElements.galleryBuilder = document.querySelector('#gallery-builder');
      }
      
      const galleryBuilder = this._cachedElements.galleryBuilder;
      if (galleryBuilder) {
        galleryBuilder.dispatchEvent(new CustomEvent('gallery-add-to-cart', {
          detail: { payload }
        }));
      }

      this.closeModal();
    }
  };
}

export function galleryVariantModal() {
  const component = {};
  const modules = [
    createGalleryVariantModalCore(),
    createGalleryVariantSelector(),
    createGalleryVariantPreloader()
  ];

  modules.forEach((module) => {
    const descriptors = Object.getOwnPropertyDescriptors(module);
    Object.defineProperties(component, descriptors);
  });

  return component;
}

